import React, { useState, useEffect } from 'react';
import { X, Send, FileText, MapPin, Box } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from './axiosConfig';
import toast from 'react-hot-toast';

const CreateWorkOrderModal = ({ isOpen, onClose, initialData = {} }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    description: '',
    assetnum: initialData.assetnum || null,
    location_id: initialData.location_id || null,
    site_id: initialData.site_id || 'BEDFORD',
    org_id: initialData.org_id || 'EAGLE'
  });

  useEffect(() => {
    if (isOpen) {
      setFormData(prev => ({
        ...prev,
        assetnum: initialData.assetnum || null,
        location_id: initialData.location_id || null,
        site_id: initialData.site_id || 'BEDFORD',
        org_id: initialData.org_id || 'EAGLE'
      }));
    }
  }, [isOpen, initialData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const numRes = await api.get(`/wo/next-number/${formData.org_id}`);
      const wonum = numRes.data.next_wonum;

      const payload = {
        wonum,
        description: formData.description,
        status: 'WAPPR',
        wo_type: 'CM',
        assetnum: formData.assetnum || null,
        location_id: formData.location_id || null,
        site_id: formData.site_id,
        org_id: formData.org_id
      };

      await api.post('/workorder/save', payload);
      toast.success(t('createWOModal.successMsg', { wonum }));
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t('createWOModal.failMsg'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-surface-card w-[500px] rounded-lg shadow-2xl overflow-hidden animate-in zoom-in duration-200 border border-line-subtle">
        <div className="bg-primary-dark text-ink-inverse p-4 flex justify-between items-center">
          <h2 className="font-black flex items-center gap-2 uppercase tracking-tighter text-sm">
            <FileText size={18} className="text-accent" /> {t('createWOModal.createTitle')}
          </h2>
          <button onClick={onClose} className="hover:bg-ink-inverse/20 p-1 rounded-full transition-colors"><X size={20}/></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-ink-secondary uppercase tracking-widest">{t('createWOModal.summaryDescription')}</label>
            <input
              required
              placeholder={t('createWOModal.summaryPlaceholder')}
              className="border-b-2 border-line-subtle focus:border-primary outline-none py-2 font-bold text-ink transition-all"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[10px] font-bold text-ink-secondary uppercase tracking-widest text-right">{t('createWOModal.contextInfo')}</label>
            <div className="flex justify-end items-center gap-2 mt-1">
              {initialData.assetnum ? (
                <div className="flex items-center gap-1 bg-primary-soft text-primary px-2 py-1 rounded border border-primary/20 font-black text-[10px]">
                  <Box size={12}/> {initialData.assetnum}
                </div>
              ) : initialData.location_id ? (
                <div className="flex items-center gap-1 bg-accent-soft text-accent px-2 py-1 rounded border border-accent/20 font-black text-[10px]">
                  <MapPin size={12}/> {initialData.location_id}
                </div>
              ) : (
                <span className="text-ink-secondary italic text-[10px]">{t('createWOModal.generalWO')}</span>
              )}
            </div>
          </div>

          <div className="pt-4 flex gap-2">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary text-ink-inverse py-2.5 rounded font-black hover:bg-primary-hover transition-all flex items-center justify-center gap-2 shadow-lg shadow-primary/20 disabled:opacity-50"
            >
              {loading ? <div className="animate-spin h-4 w-4 border-2 border-white/30 border-t-white rounded-full"/> : <Send size={16}/>}
              {t('createWOModal.submitButton')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateWorkOrderModal;
